import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudTask, CloudProject } from '../../types'
import { TASK_STATUS_LABELS, TASK_PRIORITY_LABELS, TASK_PRIORITY_COLORS, TASK_TYPE_LABELS } from '../../types'
import { listCloudTasks, listCloudProjects, upsertCloudTask, deleteCloudTask } from '../../services/api'
import { formatDate, toLocalIso, localDatePart, localTimePart, localDateKey, DetailRow, refreshBtnStyle, tabStyle, detailPanelStyle } from './shared'
import { ConfirmDialog, SaveButton, DeleteButton, InlineInput, inputStyle, selectStyle } from './CrudHelpers'

type CalendarTab = 'month' | 'day'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l }))
const STATUS_OPTIONS = Object.entries(TASK_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))
const TYPE_OPTIONS = Object.entries(TASK_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))

export function CalendarView() {
  const [tasks, setTasks] = useState<CloudTask[]>([])
  const [projects, setProjects] = useState<CloudProject[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<CalendarTab>('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedTask, setSelectedTask] = useState<CloudTask | null>(null)
  const [editingTask, setEditingTask] = useState<CloudTask | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, p] = await Promise.all([listCloudTasks(), listCloudProjects()])
      setTasks(t)
      setProjects(p)
    } catch (e) {
      console.error('Failed to load tasks:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSelectTask = (task: CloudTask) => {
    if (selectedTask?.id === task.id) {
      setSelectedTask(null)
      setEditingTask(null)
      setIsDirty(false)
    } else {
      setSelectedTask(task)
      setEditingTask({ ...task })
      setIsDirty(false)
    }
  }

  const updateField = (field: string, value: unknown) => {
    if (!editingTask) return
    setEditingTask({ ...editingTask, [field]: value })
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!editingTask) return
    setSaving(true)
    try {
      const updated = { ...editingTask, lastUpdatedAt: new Date().toISOString() }
      await upsertCloudTask(updated)
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t))
      setSelectedTask(updated)
      setEditingTask({ ...updated })
      setIsDirty(false)
    } catch (e) {
      console.error('Failed to save task:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTask) return
    try {
      await deleteCloudTask(selectedTask.id)
      setTasks(prev => prev.filter(t => t.id !== selectedTask.id))
      setSelectedTask(null)
      setEditingTask(null)
      setConfirmDelete(false)
    } catch (e) {
      console.error('Failed to delete task:', e)
    }
  }

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1))
  const prevDay = () => setCurrentDate(new Date(currentDate.getTime() - 86400000))
  const nextDay = () => setCurrentDate(new Date(currentDate.getTime() + 86400000))
  const goToday = () => setCurrentDate(new Date())

  const getTasksForDate = (date: Date): CloudTask[] => {
    const dateStr = localDateKey(date)
    return tasks.filter(t => {
      if (t.dueDate) {
        return localDatePart(t.dueDate) === dateStr
      }
      if (t.scheduledTime) {
        return localDatePart(t.scheduledTime) === dateStr
      }
      return false
    })
  }

  const buildMonthGrid = () => {
    const firstDay = new Date(year, month, 1)
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const cells: Array<{ date: Date | null; day: number }> = []
    for (let i = 0; i < startDow; i++) cells.push({ date: null, day: 0 })
    for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), day: d })
    while (cells.length % 7 !== 0) cells.push({ date: null, day: 0 })
    return cells
  }

  const todayStr = localDateKey(new Date())
  const selectedStr = localDateKey(currentDate)

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <button onClick={() => setActiveTab('month')} style={tabStyle(activeTab === 'month')}>Month</button>
          <button onClick={() => setActiveTab('day')} style={tabStyle(activeTab === 'day')}>Day</button>
          <span style={{ flex: 1 }} />
          <button onClick={goToday} style={{ ...refreshBtnStyle, margin: '0 4px' }}>Today</button>
          <button onClick={load} style={{ ...refreshBtnStyle, margin: '0 8px' }} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {activeTab === 'month' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 }}>
                <button onClick={prevMonth} style={refreshBtnStyle}>{'\u{25C0}'}</button>
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  {currentDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
                </span>
                <button onClick={nextMonth} style={refreshBtnStyle}>{'\u{25B6}'}</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                {WEEKDAYS.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'rgba(250,250,250,0.4)', padding: 4, fontWeight: 600 }}>{d}</div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {buildMonthGrid().map((cell, i) => {
                  if (!cell.date) return <div key={i} style={emptyCellStyle} />
                  const cellDateStr = localDateKey(cell.date)
                  const dayTasks = getTasksForDate(cell.date)
                  const isToday = cellDateStr === todayStr
                  const isSelected = cellDateStr === selectedStr

                  return (
                    <div
                      key={i}
                      onClick={() => { setCurrentDate(cell.date!); setActiveTab('day') }}
                      style={{
                        ...dayCellStyle,
                        border: isSelected ? '2px solid #2DA5A0' : isToday ? '2px solid rgba(45,165,160,0.4)' : '2px solid transparent',
                        background: isToday ? 'rgba(45,165,160,0.1)' : 'rgba(0,51,68,0.4)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: isToday ? 700 : 400 }}>{cell.day}</span>
                      {dayTasks.length > 0 && (
                        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 4 }}>
                          {dayTasks.slice(0, 3).map((t, j) => (
                            <div key={j} style={{ width: 6, height: 6, borderRadius: 3, background: TASK_PRIORITY_COLORS[t.priority ?? 0] || '#555' }} />
                          ))}
                          {dayTasks.length > 3 && <span style={{ fontSize: 9, color: 'rgba(250,250,250,0.4)' }}>+{dayTasks.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {activeTab === 'day' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 }}>
                <button onClick={prevDay} style={refreshBtnStyle}>{'\u{25C0}'}</button>
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  {currentDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <button onClick={nextDay} style={refreshBtnStyle}>{'\u{25B6}'}</button>
              </div>

              <DayTimeline tasks={getTasksForDate(currentDate)} onSelectTask={handleSelectTask} selectedTaskId={selectedTask?.id} />
            </div>
          )}
        </div>
      </div>

      {/* Task detail/edit panel */}
      {editingTask && (
        <div style={detailPanelStyle}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Task</span>
            <SaveButton dirty={isDirty} saving={saving} onClick={handleSave} />
            <DeleteButton onClick={() => setConfirmDelete(true)} />
            <button onClick={() => { setSelectedTask(null); setEditingTask(null); setIsDirty(false) }} style={closeBtnStyle}>X</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Title</label>
              <InlineInput value={editingTask.title || ''} onChange={v => updateField('title', v)} placeholder="Task title" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Description</label>
              <InlineInput type="textarea" value={editingTask.description || ''} onChange={v => updateField('description', v)} placeholder="Description" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Status</label>
              <select style={selectStyle} value={String(editingTask.status ?? 0)} onChange={e => updateField('status', Number((e.target as HTMLSelectElement).value))}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Priority</label>
              <select style={selectStyle} value={String(editingTask.priority ?? 0)} onChange={e => updateField('priority', Number((e.target as HTMLSelectElement).value))}>
                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Type</label>
              <select style={selectStyle} value={String(editingTask.type ?? 0)} onChange={e => updateField('type', Number((e.target as HTMLSelectElement).value))}>
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={fieldLabelStyle}>Due Date</label>
                <input type="date" style={inputStyle}
                  value={localDatePart(editingTask.dueDate)}
                  onInput={e => {
                    const date = (e.target as HTMLInputElement).value
                    const oldTime = localTimePart(editingTask.dueDate) || '00:00'
                    updateField('dueDate', date ? toLocalIso(new Date(`${date}T${oldTime}`)) : undefined)
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={fieldLabelStyle}>Due Time</label>
                <input type="time" style={inputStyle}
                  value={localTimePart(editingTask.dueDate)}
                  onInput={e => {
                    const time = (e.target as HTMLInputElement).value
                    const oldDate = localDatePart(editingTask.dueDate) || localDateKey(new Date())
                    updateField('dueDate', toLocalIso(new Date(`${oldDate}T${time || '00:00'}`)))
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Location</label>
              <InlineInput value={editingTask.location || ''} onChange={v => updateField('location', v || undefined)} placeholder="Location" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Project</label>
              <select style={selectStyle} value={editingTask.projectId || ''} onChange={e => updateField('projectId', (e.target as HTMLSelectElement).value || undefined)}>
                <option value="">No project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Tags</label>
              <InlineInput value={(editingTask.tags || []).join(', ')} onChange={v => updateField('tags', v ? v.split(',').map(t => t.trim()).filter(Boolean) : [])} placeholder="tag1, tag2, ..." />
            </div>
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
              <DetailRow label="Created" value={formatDate(editingTask.createdAt)} />
              <DetailRow label="Updated" value={formatDate(editingTask.lastUpdatedAt)} />
              {editingTask.completedAt && <DetailRow label="Completed" value={formatDate(editingTask.completedAt)} />}
            </div>
          </div>
        </div>
      )}

      {confirmDelete && selectedTask && (
        <ConfirmDialog
          message={`Delete "${selectedTask.title || 'Untitled'}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

function DayTimeline({ tasks, onSelectTask, selectedTaskId }: {
  tasks: CloudTask[]
  onSelectTask: (task: CloudTask) => void
  selectedTaskId?: string
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i)

  const getTasksForHour = (hour: number): CloudTask[] => {
    return tasks.filter(t => {
      if (t.isAllDay) return false
      const timeStr = t.scheduledTime || t.dueDate
      if (!timeStr) return hour === 9
      try { return new Date(timeStr).getHours() === hour } catch { return false }
    })
  }

  const allDayTasks = tasks.filter(t => t.isAllDay)
  const timedTasks = tasks.filter(t => !t.isAllDay)

  return (
    <div>
      {allDayTasks.length > 0 && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: 'rgba(0,51,68,0.6)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)', marginBottom: 6 }}>ALL DAY</div>
          {allDayTasks.map(t => (
            <div
              key={t.id}
              onClick={() => onSelectTask(t)}
              style={{
                fontSize: 13, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', borderRadius: 4, marginBottom: 2,
                background: selectedTaskId === t.id ? 'rgba(45,165,160,0.2)' : 'transparent',
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: 4, background: TASK_PRIORITY_COLORS[t.priority ?? 0] || '#555', flexShrink: 0 }} />
              {t.emoji && <span>{t.emoji}</span>}
              <span style={{ flex: 1 }}>{t.title}</span>
            </div>
          ))}
        </div>
      )}

      {hours.map(hour => {
        const hourTasks = getTasksForHour(hour)
        return (
          <div key={hour} style={{ display: 'flex', minHeight: 40, borderBottom: '1px solid rgba(250,250,250,0.05)' }}>
            <div style={{ width: 50, flexShrink: 0, fontSize: 12, color: 'rgba(250,250,250,0.35)', padding: '4px 8px 0 0', textAlign: 'right' }}>
              {String(hour).padStart(2, '0')}:00
            </div>
            <div style={{ flex: 1, padding: '2px 0' }}>
              {hourTasks.map(t => (
                <div
                  key={t.id}
                  onClick={() => onSelectTask(t)}
                  style={{
                    fontSize: 13, padding: '6px 8px', marginBottom: 2, borderRadius: 4, cursor: 'pointer',
                    background: selectedTaskId === t.id
                      ? 'rgba(45,165,160,0.2)'
                      : `${TASK_PRIORITY_COLORS[t.priority ?? 0] || '#555'}20`,
                    borderLeft: `3px solid ${TASK_PRIORITY_COLORS[t.priority ?? 0] || '#555'}`,
                  }}
                >
                  {t.emoji && <span style={{ marginRight: 4 }}>{t.emoji}</span>}
                  {t.title}
                  {t.duration && t.duration !== 60 && <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)', marginLeft: 6 }}>{t.duration}min</span>}
                </div>
              ))}
            </div>
          </div>
        )
      })}
      {timedTasks.length === 0 && allDayTasks.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)', fontSize: 14 }}>No tasks for this day</div>
      )}
    </div>
  )
}

const dayCellStyle: Record<string, string | number> = { padding: 6, borderRadius: 6, minHeight: 48 }
const emptyCellStyle: Record<string, string | number> = { padding: 6, borderRadius: 6, minHeight: 48 }
const closeBtnStyle: Record<string, string | number> = {
  padding: '4px 10px', border: '1px solid rgba(250,250,250,0.15)', borderRadius: 6,
  background: 'transparent', color: 'rgba(250,250,250,0.6)', cursor: 'pointer', fontSize: 12,
}
const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12, color: 'rgba(250,250,250,0.5)', marginBottom: 4, display: 'block',
}
